import { Component, ChangeDetectionStrategy, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, NonNullableFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { OpenClose, PopupBaseComponent } from '@app/shared/popup';
import { environment } from '@env/environment';
import { FormValidators } from '@shared/forms';
import { BehaviorSubject, combineLatest, forkJoin, Subscription } from 'rxjs';
import { filter, finalize, map } from 'rxjs/operators';
import { PartnerModel } from './model';
import { HttpService } from './service/http.service';
import { PartnersService } from './service/partners.service';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { NgClass, AsyncPipe } from '@angular/common';
import { LocalizePipe } from '../../../../i18n/pipes/lozalize.pipe';

@Component({
    selector: 'app-metrix-configurator-popup',
    templateUrl: './metrix-configurator-popup.component.html',
    styleUrls: ['./metrix-configurator-popup.component.scss'],
    animations: [OpenClose],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [PartnersService],
    imports: [ReactiveFormsModule, TranslateDirective, RouterLink, NgClass, AsyncPipe, TranslatePipe, LocalizePipe],
})
export class MetrixConfiguratorPopupComponent extends PopupBaseComponent implements OnDestroy, OnInit {
    private httpService = inject(HttpService);
    private partnersService = inject(PartnersService);
    private formBuilder = inject(NonNullableFormBuilder);

    override data!: {
        id: string;
    };

    metrixUrl = environment.metrixUrl;

    isButtonDisabled = true;

    form = this.formBuilder.group({
        voivodeship: this.formBuilder.control('', [Validators.required]),
        partner: new FormControl<PartnerModel | null>(null, { validators: [Validators.required] }),
        phone: this.formBuilder.control('', [FormValidators.Phone]),
    });

    phoneFormControl = this.form.controls.phone;

    get selectedPartner(): PartnerModel | null {
        return this.form.controls.partner.value;
    }

    voivodeships$ = combineLatest([
        this.partnersService.partners$.pipe(filter((data) => data !== null)),
        this.partnersService.voivodeships$.pipe(filter((data) => data !== null)),
    ]).pipe(
        map(([partners, voivodeships]) => {
            return partners
                .map((partner) => partner.address.voivodeship)
                .filter((v, i, self) => self.indexOf(v) === i)
                .map((code) => ({
                    name: voivodeships.find((voivodeship) => voivodeship.terytCode === code)?.namePl,
                    code,
                }));
        }),
    );

    partners$ = this.partnersService.partners$.pipe(
        map((partners) => {
            return partners.filter((partner) => partner.address.voivodeship === this.form.controls.voivodeship.value);
        }),
    );

    saving$ = new BehaviorSubject(false);

    /**
     * Subscription
     */
    private subscriptions$ = new Subscription();

    ngOnInit() {
        this.partnersService.init();
    }

    ngOnDestroy(): void {
        this.subscriptions$.unsubscribe();
    }

    onClickVoivodeship(event: Event, voivodeship: string): void {
        event.preventDefault();
        this.form.controls.voivodeship.patchValue(voivodeship);
    }

    onClickPartner(event: Event, partner: PartnerModel): void {
        event.preventDefault();
        this.form.controls.partner.patchValue(partner);
    }

    onPDFDownloadClick(event: MouseEvent): void {
        event.preventDefault();
        this.saving$.next(true);
        const partner = <PartnerModel>this.form.controls.partner.value;
        this.subscriptions$.add(
            forkJoin([this.httpService.printPdf(this.data.id, partner), this.httpService.createLead(this.data.id, partner.id, this.form.controls.phone.value)])
                .pipe(finalize(() => this.saving$.next(false)))
                .subscribe(),
        );
    }

    onPartnersBackClick(event: MouseEvent): void {
        event.preventDefault();
        this.form.controls.voivodeship.patchValue('');
    }

    onPDFBackClick(event: MouseEvent): void {
        event.preventDefault();
        this.form.controls.partner.patchValue(null);
    }

    toggleButtonAccess(e: Event): void {
        this.isButtonDisabled = (<{ checked: boolean }>(<unknown>e.target))?.checked === false;
    }

    /**
     * Close Click
     */
    closeClick(): void {
        this.closeSubject.next('event.click');
    }
}
